<template>
  <div class="h-full flex">
    <PageSidebar
      title="Growth"
      :items="sidebarItems"
      width="w-48"
      bg-class="bg-gray-100"
      :show-collapse-icon="false"
      @item-click="onSidebarClick"
    />

    <div class="flex-1 h-full overflow-y-auto bg-white">
      <div>
        <TargetsTab
          v-if="activeTab === 'targets'"
          key="targets"
          :initial-territory="territory"
          @open-brief="openBrief"
          @territory-change="territory = $event"
        />
        <AccountsTab
          v-else-if="activeTab === 'accounts'"
          key="accounts"
          :connect-result="salesforceResult"
          @open-brief="openBrief"
        />
        <EngagementsTab v-else-if="activeTab === 'engagements'" key="engagements" />
        <BenchmarksTab v-else-if="activeTab === 'benchmarks'" key="benchmarks" />
        <AgentPageView
          v-else-if="activeTab === 'report' && selectedReport"
          :id="selectedReport"
          :key="`report-${selectedReport}`"
        />
        <BriefTab
          v-else-if="activeTab === 'brief'"
          key="brief"
          :practice-key="selectedKey"
          :tag-labels="tagLabels"
          @back="activeTab = returnTab"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PageSidebar from '@/components/widgetcomponents/library/PageSidebar.vue'
import TargetsTab from './TargetsTab.vue'
import BriefTab from './BriefTab.vue'
import AccountsTab from './AccountsTab.vue'
import AgentPageView from '@/components/Beta/AgentPageView.vue'
import EngagementsTab from './EngagementsTab.vue'
import BenchmarksTab from './BenchmarksTab.vue'

export default {
  name: 'GrowthDashboard',
  components: { PageSidebar, TargetsTab, BriefTab, AccountsTab, AgentPageView, EngagementsTab, BenchmarksTab },
  data() {
    return {
      activeTab: ['accounts', 'report', 'engagements', 'benchmarks'].includes(this.$route.query.tab) ? this.$route.query.tab : 'targets',
      selectedReport: Number(this.$route.query.report) || null,
      reportsOpen: this.$route.query.tab === 'report',
      reports: [],
      returnTab: 'targets',
      tagLabels: {},
      salesforceResult: this.$route.query.salesforce || null,
      selectedKey: this.$route.query.practice || null,
      territory: this.$route.query.state || null,
    }
  },
  computed: {
    sidebarItems() {
      const iconClass = 'text-gray-500 text-[13px] mr-1'
      const laterIconClass = 'text-gray-300 text-[13px] mr-1'
      const later = { disabled: true, badge: 'later', badgeClass: 'bg-gray-100 text-gray-400', iconClass: laterIconClass }
      return [
        { id: 'markets', label: 'Markets', icon: ['fas', 'map'], ...later },
        { id: 'targets', label: 'Targets', active: this.activeTab === 'targets' || (this.activeTab === 'brief' && this.returnTab === 'targets'), icon: ['fas', 'bullseye'], iconClass },
        { id: 'accounts', label: 'Accounts', active: this.activeTab === 'accounts' || (this.activeTab === 'brief' && this.returnTab === 'accounts'), icon: ['fas', 'building'], iconClass },
        { id: 'engagements', label: 'Engagements', active: this.activeTab === 'engagements', icon: ['fas', 'comments'], iconClass },
        { id: 'benchmarks', label: 'Benchmarks', active: this.activeTab === 'benchmarks', icon: ['fas', 'chart-line'], iconClass },
        {
          id: 'reports', label: 'Reports', icon: ['fas', 'chart-bar'], iconClass,
          expanded: this.reportsOpen,
          alignChildren: true,
          children: this.reports.map((r) => ({
            id: `report:${r.id}`, label: r.title, pageId: r.id,
            active: this.activeTab === 'report' && this.selectedReport === r.id,
          })),
        },
        { id: 'deals', label: 'Deals', icon: ['fas', 'handshake'], ...later },
        { id: 'network', label: 'Network', icon: ['fas', 'sitemap'], ...later },
        { id: 'forecast', label: 'Forecast', icon: ['fas', 'chart-line'], ...later },
      ]
    },
  },
  watch: {
    activeTab(tab) {
      const query = { ...this.$route.query }
      if (tab === 'brief' && this.selectedKey) query.practice = this.selectedKey
      else delete query.practice
      if (['accounts', 'report', 'engagements', 'benchmarks'].includes(tab)) query.tab = tab
      else delete query.tab
      if (tab === 'report' && this.selectedReport) query.report = this.selectedReport
      else delete query.report
      delete query.salesforce
      this.$router.replace({ query })
    },
    territory(v) {
      const query = { ...this.$route.query }
      if (v) query.state = v
      else delete query.state
      this.$router.replace({ query })
    },
  },
  async created() {
    if (this.selectedKey) this.activeTab = 'brief'
    this.loadReports()
    const { data } = await this.$axios.get('/api/growth/briefs/', { params: { limit: 0 } })
    this.tagLabels = Object.fromEntries((data.tag_options || []).map((t) => [t.key, t.label]))
  },
  methods: {
    async loadReports() {
      const { data } = await this.$axios.get('/api/impact_analytics/agent-pages/', { params: { category: 'growth_reports' } })
      this.reports = data
    },
    onSidebarClick(item) {
      if (item.disabled) return
      if (item.children) {
        this.reportsOpen = !this.reportsOpen
        return
      }
      if (item.pageId) {
        this.selectedReport = item.pageId
        if (this.activeTab === 'report') this.$router.replace({ query: { ...this.$route.query, report: item.pageId } })
        this.activeTab = 'report'
        return
      }
      this.activeTab = item.id
    },
    openBrief(target) {
      this.returnTab = this.activeTab === 'accounts' ? 'accounts' : 'targets'
      this.selectedKey = target.practice_key
      this.activeTab = 'brief'
    },
  },
}
</script>

